import dayjs from 'dayjs';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, TouchableOpacity } from 'react-native';
import Toast from 'react-native-toast-notifications';

import CachedImage from '@components/CachedImage/CachedImage';
import { TeamsModal } from '@components/FormModals/TeamsModal';
import { storageUsedColor } from '@components/Profile/SubscriptionPlanDetail.web';
import {
  DeferredInvitation,
  InviteMemberModal,
} from '@components/Profile/TeamDetailPage/InviteMemberModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Skeleton } from '@components/shared/Skeleton';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupSubscriptionButtonWeb } from '@components/Web/PopupSubscriptionButton.web';
import { LocalFile, ProjectMemberRole, Team } from '@graphql/generated';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { FormValues } from '@navigation/payments/create-subscription-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { createTeamSchema } from '@schemas/createTeamSchema';
import { SESSION_SELECTED_TEAM_NAME } from '@src/constants/constants';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { getTeamLabel } from '@utils/teams/getTeamLabel';

// TA-10280: 2 skeleton cards while billing data fetches — logo placeholder +
// name line + one line per real info row (Plan / Plan Status / Storage Used /
// Total Team Members / Total Amount / Payment Method), matching `renderItem`
// below row-for-row per the global "match column count of real content" rule.
const SubscriptionCardSkeleton: React.FC = () => (
  <Box
    borderRadius='xs'
    borderWidth={1}
    borderColor='grey02'
    mb='m'
    pb='m'
    px='m'>
    <Box flexDirection='row' alignItems='center' my='m'>
      <Skeleton variant='rect' width={40} height={40} borderRadius='xs' />
      <Box flex={1} ml='xs'>
        <Skeleton variant='text' width='50%' />
      </Box>
    </Box>
    {Array.from({ length: 6 }, (_, index) => (
      <Box
        key={index}
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        py='xs'
        height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}>
        <Skeleton variant='text' width={90} />
        <Skeleton variant='text' width={70} />
      </Box>
    ))}
  </Box>
);

export const SubscriptionPlan: React.FC = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const navigation = useAppNavigation();
  const { setSelectedTeam } = useWebDrawerNavigator();
  const [isHoveredTitleId, setIsHoveredTitleId] = useState<string | undefined>(
    undefined
  );
  const [isHoveredId, setIsHoveredId] = useState<string | undefined>(undefined);
  const [panelWidth, setPanelWidth] = useState<number>(1040);

  const [isHovered, setIsHovered] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [navigatedFromCreateTeam, setNavigatedFromCreateTeam] = useState(false);
  const [activeOnly, setActiveOnly] = useState<boolean>(true);

  const { teams, refetch } = useListTeamsFromQuery({ includePending: true });
  // Billing data is split out of listTeams (TA-9873) and only fetched here
  const { billingByTeamId, loading: billingLoading } = useTeamBillingFromQuery({
    includePending: true,
  });
  const isBillingReady =
    !billingLoading || Object.keys(billingByTeamId).length > 0;

  const users = () => {
    if (!me) {
      return [];
    }
    const owner = {
      id: me.id,
      avatar: me.avatar,
      name: me.name,
      firstName: me.firstName,
      lastName: me.lastName,
      role: ProjectMemberRole.Owner,
    };
    return [owner];
  };
  const {
    setUpgradeTeamPlanOpen,
    setIsTeamPersonal,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setTeamValues,
    setUpgradeConfirmModalOpen,
    subscriptionCreatedModalOpen,
  } = useWebDrawer();

  const toastRef = useRef(null);

  useEffect(() => {
    if (subscriptionCreatedModalOpen) {
      refetch();
    }
  }, [subscriptionCreatedModalOpen, refetch]);

  window.sessionStorage.setItem('goto_welcome', '0');

  // Team creation is deferred (TA-10009): the backend creates the team from
  // teamAttributes when createStripeSubscription runs after checkout, so
  // abandoning the flow leaves no orphaned team behind.
  const submitForm = (
    values: FormValues,
    setSubmitting: (a: boolean) => void
  ) => {
    setSubmitting(true);

    // Only proceed if name is provided as it is required.
    if (values.name) {
      setUUIdTeamOpen('');
      setShowCreateTeamModal(false);
      setShowMemberModal(true);
      setNavigatedFromCreateTeam(true);
    }

    setIsTeamPersonal(false);
    setTeamValues(values);
    setSubmitting(false);
  };

  const initialValues: FormValues = {
    users: users(),
    teamId: null,
    plan: '',
    planName: '',
    billingCycle: 'year',
    paymentComplete: false,
    priceId: undefined,
    price: 0,
    location: null,
    name: '',
    skillIds: [],
    logo: null,
  };

  const sortedTeams = (
    teams
      ? [...teams].sort((teamA, teamB) => {
          // Compare team titles, move "Personal Projects" to the top
          if (teamA.personal) return -1;
          if (teamB.personal) return 1;
          // Sort other teams alphabetically
          return teamA.name.localeCompare(teamB.name);
        })
      : []
  ).filter((t) => {
    const expire = dayjs().isAfter(
      dayjs(billingByTeamId[t.id]?.subscriptionDetails?.expirationDate)
    );

    return activeOnly ? !expire : expire;
  });

  const maskEmail = (email: string) => {
    if (!email.includes('@')) return '';
    const [localPart, domain] = email.split('@');

    if (domain.length < 10) {
      return `${localPart[0]}***@${domain}`;
    } else {
      return `${localPart[0]}***@${domain.substring(
        0,
        3
      )} ... ${domain.substring(domain.length - 5)}`;
    }
  };

  const renderItem = (team: Team, index: number) => {
    const subscriptionDetails = billingByTeamId[team.id]?.subscriptionDetails;
    let planName = '-';

    if (subscriptionDetails?.subscriptionPlanName) {
      planName = subscriptionDetails?.subscriptionPlanName;
    } else if (
      team?.personal &&
      subscriptionDetails?.subscriptionPlanName == null
    ) {
      planName = 'Team';
    }

    const getLabel = getTeamLabel({ ...team, subscriptionDetails } as Team);
    const planStatusLabelColor =
      getLabel === 'Renews' || getLabel === 'Free Forever'
        ? 'greenSecondary'
        : 'alertRed';

    const paymentCardBrand = (
      subscriptionDetails?.paymentCardBrand ?? ''
    ).toLowerCase();

    const totalSizeAvaiblePlan = team
      ? Math.max(0, parseInt(team?.maxDocumentStorage))
      : 0;

    const storageAvailable = team
      ? Math.max(0, parseInt(team?.storageRemaining))
      : 0;

    return (
      <Box
        key={team.id}
        onMouseEnter={() => setIsHoveredId(team.id)}
        onMouseLeave={() => setIsHoveredId(undefined)}
        borderRadius='xs'
        borderWidth={1}
        borderColor='grey02'
        style={{ marginLeft: index % countPerRow == 0 ? 24 : 18 }}
        backgroundColor={isHoveredId === team.id ? 'grey01' : 'white'}
        mb='m'
        pb='m'
        px='m'>
        <TouchableOpacity
          onPress={() => {
            setSelectedTeam(team);
            setUpgradeTeamPlanOpen(false);
            window.sessionStorage.setItem(
              SESSION_SELECTED_TEAM_NAME,
              '1' + team.name
            );
            navigation.navigateToMyprofileSubscriptionDetail(team.uuid);
          }}>
          <Box
            flexDirection='row'
            alignItems='center'
            my='m'
            onMouseEnter={() => setIsHoveredTitleId(team.id)}
            onMouseLeave={() => setIsHoveredTitleId(undefined)}>
            {!team.avatarFile &&
              (team.personal ? (
                <Box
                  width={40}
                  height={40}
                  alignItems='center'
                  justifyContent='center'
                  borderRadius='xs'>
                  <Icon
                    variant='xl'
                    borderRadius='xs'
                    padding='xs'
                    name='Office'
                    backgroundColor='greenSecondaryMild'
                    isCircle={false}
                  />
                </Box>
              ) : (
                <Box
                  width={40}
                  height={40}
                  backgroundColor='black'
                  borderRadius='xs'
                  alignItems='center'
                  justifyContent='center'>
                  <Icon name='TT40' width={40} height={40} />
                </Box>
              ))}

            {team.avatarFile && (
              <CachedImage
                image={team.avatarFile as unknown as LocalFile}
                width={40}
                height={40}
                borderRadius={theme.spacing.xs}
              />
            )}
            <Text
              variant='labelEmphasized'
              color='textPrimary'
              ml='xs'
              flex={1}
              numberOfLines={1}
              textDecorationLine={
                isHoveredTitleId === team.id ? 'underline' : undefined
              }>
              {team.name}
            </Text>
          </Box>
        </TouchableOpacity>

        <Box
          flexDirection='row'
          alignItems='center'
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}>
          <Text variant='labelSmall' color='textPrimary'>
            {t('models:profile.subscriptionPlans.plan')}
          </Text>
          <Box flex={1}></Box>
          <Box flexDirection='row' alignItems='center'>
            <Text variant='webBodySecondary' color='textSecondary'>
              {planName +
                (team?.personal &&
                dayjs(team?.createdAt).add(1, 'month') <= dayjs()
                  ? ' (Trial)'
                  : '')}
            </Text>
          </Box>
        </Box>

        <Box py='xs' flexDirection='row' justifyContent='flex-start'>
          <Text variant='labelSmall' color='textPrimary'>
            {t('models:profile.subscriptionPlans.planStatus')}
          </Text>
          <Box flex={1}></Box>
          <Text variant='webBodySecondary' color={planStatusLabelColor}>
            {team?.personal || !subscriptionDetails
              ? getLabel
              : `${getLabel} on ${dayjs(
                  subscriptionDetails?.expirationDate
                ).format('MMM DD, YYYY')}`}
          </Text>
        </Box>
        <Box py='xs' flexDirection='row' justifyContent='flex-start'>
          <Text variant='labelSmall' color='textPrimary'>
            {t('models:profile.subscriptionPlans.storageUsed')}
          </Text>
          <Box flex={1}></Box>
          <Box flexDirection='row' alignItems='center'>
            {totalSizeAvaiblePlan > 0 && (
              <Text
                variant='webBodySecondary'
                color={storageUsedColor(
                  storageAvailable,
                  totalSizeAvaiblePlan
                )}>
                {t('format:fileSize', {
                  val: totalSizeAvaiblePlan - storageAvailable,
                })}{' '}
                / {t('format:fileSize', { val: totalSizeAvaiblePlan })}
              </Text>
            )}
          </Box>
        </Box>
        <Box
          flexDirection='row'
          alignItems='center'
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}>
          <Text variant='labelSmall' color='textPrimary'>
            {t('models:teams.subscriptions.totalTeamMembers')}
          </Text>
          <Box flex={1}></Box>
          <Box flexDirection='row' alignItems='center'>
            <Text variant='webBodySecondary' color='textSecondary'>
              {team.usersCount ?? ''}
            </Text>
          </Box>
        </Box>
        <Box
          flexDirection='row'
          alignItems='center'
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}>
          <Text variant='labelSmall' color='textPrimary'>
            {t('models:teams.subscriptions.totalAmount')}
          </Text>
          <Box flex={1}></Box>
          <Box flexDirection='row' alignItems='center'>
            <Text variant='webBodySecondary' color='textSecondary'>
              {subscriptionDetails?.pricePerSeat
                ? `$${(
                    (Number(subscriptionDetails?.pricePerSeat) / 100) *
                    (team?.usersCount ?? 1)
                  ).toFixed(2)}`
                : '-'}
            </Text>
          </Box>
        </Box>
        <Box
          flexDirection='row'
          alignItems='center'
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}>
          <Text variant='labelSmall' color='textPrimary'>
            {t('models:profile.subscriptionPlans.paymentMethod')}
          </Text>

          <Box flex={1}></Box>
          {/*about link payment brand is email address.*/}
          {paymentCardBrand.includes('@') && (
            <Box flexDirection='row' alignItems='center'>
              <Icon
                marginTop='xxs'
                name='LinkPayment'
                variant='m'
                color='white'
              />
              <Text
                ml='xxs'
                variant='webBodySecondary'
                color='textSecondary'
                numberOfLines={1}>
                {maskEmail(subscriptionDetails?.paymentCardBrand ?? '')}
              </Text>
            </Box>
          )}
          {subscriptionDetails?.paymentCardLast4 ? (
            <Box flexDirection='row' justifyContent='center' marginTop='xxs'>
              {paymentCardBrand == 'visa' && (
                <Icon name='Visa' variant='xl' color='white' />
              )}
              {paymentCardBrand === 'mastercard' && (
                <Icon name='MasterCard' variant='xl' color='white' />
              )}
              {paymentCardBrand === 'amex' && (
                <Icon name='AmericanExpress' variant='xl' color='white' />
              )}
              {paymentCardBrand === 'discover' && (
                <Icon name='Discover' variant='xl' color='white' />
              )}
              <Text
                ml='xs'
                variant='webBodySecondary'
                color='textSecondary'
                marginTop='xxs'>
                ending in {subscriptionDetails?.paymentCardLast4}
              </Text>
            </Box>
          ) : (
            <Text variant='webBodySecondary' color='textSecondary' ml='xxs'>
              -
            </Text>
          )}
        </Box>
      </Box>
    );
  };

  const offsetPopButton: Partial<any> = {
    position: 'right',
    offset: [0, 6],
  };

  // every card min width set to 320, margin to left is 18
  const countPerRow = (panelWidth - (panelWidth % 338)) / 338;
  const planRows =
    (sortedTeams.length - (sortedTeams.length % countPerRow)) / countPerRow +
    (sortedTeams.length % countPerRow > 0 ? 1 : 0);
  const rowList = Array.from({ length: planRows }, (_, index) => index + 1);

  let counter = 0;

  return (
    <Box
      width='100%'
      height='100%'
      onLayout={(event) => {
        // the start and end margin is 8 and 24
        setPanelWidth(event.nativeEvent.layout.width - 30);
      }}>
      <Box ml='l' mt='m' flexDirection='row'>
        <PopupSubscriptionButtonWeb
          title={
            activeOnly
              ? t('models:profile.subscriptionPlans.activeSubscriptions')
              : t('models:profile.subscriptionPlans.canceledSubscriptions')
          }
          boxProps={offsetPopButton}
          onPressActiveSubscriptions={() => {
            setActiveOnly(true);
          }}
          onPressCanceledSubscriptions={() => {
            setActiveOnly(false);
          }}
        />
        <Box flex={1}></Box>

        <Button
          accessibilityLabel='Create Team'
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          isSmall
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          backgroundColor={isHovered ? 'grey05' : 'black'}
          prefix={<Icon variant='s' name='Plus' color='white' />}
          onPress={() => {
            setUUIdTeamOpen('');
            setIdTeamOpen('');
            setShowCreateTeamModal(true);
          }}
          borderRadius='xs'
          borderColor={isHovered ? 'grey05' : 'black'}
          variant='create'
          marginRight='l'
          prefixMarginRight='xs'
          paddingHorizontal='m'
          paddingVertical='xs'>
          Create Team
        </Button>
      </Box>
      <Box flex={1} mt='m'>
        {!isBillingReady && (
          <Box
            testID='subscription-card-skeleton-grid'
            flexDirection='row'
            mr='l'>
            <Box flex={1} marginLeft='l'>
              <SubscriptionCardSkeleton />
            </Box>
            <Box flex={1} marginLeft='m'>
              <SubscriptionCardSkeleton />
            </Box>
          </Box>
        )}
        {isBillingReady && (
          <ScrollView showsVerticalScrollIndicator={false}>
            {rowList.map((i, index) => (
              <Box
                key={i.toString() + '_' + index.toString()}
                flexDirection='row'
                flex={1}
                justifyContent='space-between'
                alignItems='stretch'
                mr='l'>
                {sortedTeams.map(
                  (team, index) =>
                    index >= (i - 1) * countPerRow &&
                    index < i * countPerRow && (
                      <Box flex={1} key={team.id}>
                        {renderItem(team, index)}
                      </Box>
                    )
                )}
                {i === rowList.length &&
                  i * countPerRow > sortedTeams.length &&
                  Array.from(
                    { length: i * countPerRow - sortedTeams.length },
                    (_, j) => j + 1
                  ).map((_) => {
                    counter++;
                    return <Box mt='m' flex={1} key={counter}></Box>;
                  })}
              </Box>
            ))}
          </ScrollView>
        )}
      </Box>

      {showCreateTeamModal && (
        <Formik
          initialValues={initialValues}
          validationSchema={createTeamSchema}
          onSubmit={(values, { setSubmitting }) => {
            submitForm(values, setSubmitting);
          }}>
          {() => {
            return (
              <TeamsModal
                title='Team Setup'
                buttonLabel={t('shared:next')}
                onClose={() => setShowCreateTeamModal(false)}
              />
            );
          }}
        </Formik>
      )}
      {showMemberModal && (
        <InviteMemberModal
          requireMemberSelection={false}
          isVisible={showMemberModal}
          // No team exists yet (deferred creation) — cancelling just drops
          // the locally collected values, nothing to delete
          onCancel={() => {
            setShowMemberModal(false);
            setNavigatedFromCreateTeam(false);
          }}
          onClose={() => {
            setShowMemberModal(false);
            setNavigatedFromCreateTeam(false);
          }}
          teamId=''
          onDeferredSend={(members: DeferredInvitation[]) => {
            // Invitations ride along in teamValues and are sent as
            // teamAttributes.teamMembers by createStripeSubscription
            setTeamValues((prev: FormValues) => ({
              ...prev,
              teamMembers: members,
            }));
            if (navigatedFromCreateTeam) {
              setUpgradeConfirmModalOpen(true);
            }
          }}
        />
      )}
      {/* Post-subscription success UI is the shared SubscriptionCreatedModal
          rendered by MainPanel — the local trial congratulations modal was
          removed with deferred team creation (TA-10009): no team exists at
          Team Setup time, so its trial state can no longer be read here */}
      <Toast ref={toastRef} />
    </Box>
  );
};
